import type {
  DocumentTypeDetailModel,
  DocumentTypeDetailViewModel,
  FlowVersionSummaryResponse,
  FlowVersionSummaryViewModel,
  FlowConfigResponse,
  FlowConfigViewModel,
  FlowStepResponse,
  FlowStepViewModel,
  LLMFlowStepConfigResponse,
  LLMFlowStepConfigViewModel,
  OCRFlowStepConfigResponse,
  OCRFlowStepConfigViewModel,
  AgentExecutionResponse,
  AgentExecutionViewModel,
  CreateFlowConfigRequest,
  LLMConfigOptionsViewModel,
  LLMConfigOptions,
} from "@models/doctype";
import { splitModelRef } from "@utils/modelRef";

// ============================================================================
// Request Builders (camelCase to snake_case)
// ============================================================================

export const buildCreateFlowConfigRequest = (data: CreateFlowConfigRequest) => {
  return {
    flow_base_id: data.flow_base_id,
    flow_type: data.flow_type,
    flow_description: data.flow_description,
    steps: data.steps,
    preprocessing_options: data.preprocessing_options,
  };
};

// ============================================================================
// Response Builders (snake_case to camelCase)
// ============================================================================

export const buildSingleDocumentTypeDetailViewModel = (
  doctype: DocumentTypeDetailModel,
): DocumentTypeDetailViewModel => ({
  id: doctype.id,
  docTypeCode: doctype.doctype_code,
  docTypeName: doctype.doctype_name,
  logoUrl: doctype.logo_url ?? null,
  description: doctype.description,
  categoryId: doctype.category_id ?? null,
  categoryName: doctype.category_name ?? null,
  organizationId: doctype.organization_id ?? null,
  status: doctype.status,
  sharepointEnabled: doctype.sharepoint_enabled ?? false,
  mappingConfig: doctype.mapping_config
    ? {
        enabled: doctype.mapping_config.enabled,
        service: doctype.mapping_config.service ?? null,
        pluginId: doctype.mapping_config.plugin_id ?? null,
      }
    : null,
  createdAt: doctype.created_at ? new Date(doctype.created_at) : null,
  updatedAt: doctype.updated_at ? new Date(doctype.updated_at) : null,
  // Absent (older API) stays undefined: unknown is not the same as "no live flow".
  activeFlow: doctype.active_flow === undefined
    ? undefined
    : doctype.active_flow ? buildFlowVersionSummaryViewModel(doctype.active_flow) : null,
});

export const buildDocumentTypeDetailViewModel = (
  response: DocumentTypeDetailModel[],
): DocumentTypeDetailViewModel[] => {
  return response.map(buildSingleDocumentTypeDetailViewModel);
};

// ============================================================================
// Flow Configuration Builders
// ============================================================================
export const buildLLMConfigOptionsViewModel = (
  response: LLMConfigOptions,
): LLMConfigOptionsViewModel => ({
  temperature: response.temperature,
  topP: response.top_p,
  topK: response.top_k,
  maxOutputTokens: response.max_output_tokens,
  thinkingBudget: response.thinking_budget,
  seed: response.seed,
  mediaResolution: response.media_resolution,
  compactResponseSchema: response.compact_response_schema,
  inputMode: response.input_mode,
  missingOcrPolicy: response.missing_ocr_policy,
  ocrMinConfidence: response.ocr_min_confidence,
  ocrBboxMode: response.ocr_bbox_mode,
});

export const buildLLMFlowStepConfigViewModel = (
  response: LLMFlowStepConfigResponse,
): LLMFlowStepConfigViewModel => {
  const { provider, model, prefix } = splitModelRef(response.model_name);
  return {
    provider,
    modelName: model,
    modelPrefix: prefix,
    systemPrompt: response.system_prompt,
    userPrompt: response.user_prompt ?? "",
    fallbackModel: response.fallback_model,
    chunkingOptions: response.chunking_options,
    pageDetectionPolicy: response.page_detection_policy,
    configOptions: response.config_options
      ? buildLLMConfigOptionsViewModel(response.config_options)
      : null,
    responseSchema: response.response_schema ?? null,
    executions: response.executions
      ? response.executions.map(buildAgentExecutionViewModel)
      : null,
  };
};

export const buildOCRFlowStepConfigViewModel = (
  response: OCRFlowStepConfigResponse,
): OCRFlowStepConfigViewModel => {
  const { provider, model, prefix } = splitModelRef(response.model_name);
  return {
    provider,
    modelName: model,
    modelPrefix: prefix,
    ocrOptions: response.ocr_options ?? null,
  };
};

export const buildFlowStepViewModel = (
  response: FlowStepResponse,
): FlowStepViewModel => {
  return {
    stepOrder: response.step_order,
    stepType: response.step_type,
    llmStepConfig: response.llm_step_config
      ? buildLLMFlowStepConfigViewModel(response.llm_step_config)
      : undefined,
    ocrStepConfig: response.ocr_step_config
      ? buildOCRFlowStepConfigViewModel(response.ocr_step_config)
      : undefined,
  };
};

export const buildAgentExecutionViewModel = (
  response: AgentExecutionResponse,
): AgentExecutionViewModel => {
  const { provider, model, prefix } = splitModelRef(response.model_name);
  return {
    domainKey: response.domain_key,
    userPrompt: response.user_prompt,
    responseSchema: response.response_schema,
    systemPrompt: response.system_prompt ?? null,
    provider,
    modelName: model || null,
    modelPrefix: prefix,
    fallbackModel: response.fallback_model,
    configOptions: response.config_options
      ? buildLLMConfigOptionsViewModel(response.config_options)
      : null,
  };
};

export const buildFlowVersionSummaryViewModel = (
  response: FlowVersionSummaryResponse,
): FlowVersionSummaryViewModel => {
  return {
    id: response.id,
    flowBaseId: response.flow_base_id,
    version: response.version,
    isActive: response.is_active,
    flowType: response.flow_type,
    createdAt: response.created_at ? new Date(response.created_at) : null,
    updatedAt: response.updated_at ? new Date(response.updated_at) : null,
    createdBy: response.created_by ?? null,
    updatedBy: response.updated_by ?? null,
  };
};

export const buildFlowVersionSummariesViewModel = (
  responses: FlowVersionSummaryResponse[],
): FlowVersionSummaryViewModel[] => {
  return responses
    .sort((a, b) => b.version - a.version)
    .map(buildFlowVersionSummaryViewModel);
};

export const buildFlowConfigViewModel = (
  response: FlowConfigResponse,
): FlowConfigViewModel => {
  return {
    id: response.id,
    flowBaseId: response.flow_base_id,
    docTypeId: response.doctype_id,
    flowType: response.flow_type,
    flowDescription: response.flow_description,
    version: response.version,
    steps: response.steps.map(buildFlowStepViewModel),
    preprocessingOptions: response.preprocessing_options,
    isActive: response.is_active,
    updatedBy: response.updated_by,
    createdAt: response.created_at ? new Date(response.created_at) : null,
    updatedAt: response.updated_at ? new Date(response.updated_at) : null,
  };
};
